"use client";
import React from "react";
import { motion, Transition } from "motion/react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";
import HoveredLink from "src/components/HoveredLink";


const transition = {
  type: "spring",
  mass: 0.5,
  damping: 11.5,
  stiffness: 100,
  restDelta: 0.001,
  restSpeed: 0.001,
};

export const MenuItem = ({
  setActive,
  active,
  item,
  children,
}: {
  setActive: (item: string | null) => void;
  active: string | null;
  item: string;
  children?: React.ReactNode;
}) => {
  return (
    <div onMouseEnter={() => setActive(item)} className="relative">
      <span className="relative z-30 flex items-center gap-1">{item} 
        {active === item ? (
          <ChevronDown className="size-4 rotate-180 transition-all duration-200 group-hover:rotate-0" />
        ) : (
          <ChevronDown className="size-4 transition-all duration-200 group-hover:rotate-180" />
        )}
      </span>
      {active !== null && (
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={transition as Transition }
        >
          {active === item && (
            <div 
              className="absolute top-[calc(100%_+_1.2rem)] left-1/2 transform -translate-x-1/2 pt-4" 
              onMouseLeave={() => setActive(null)}
            >
              <motion.div
                transition={transition as Transition}
                layoutId="active" // layoutId ensures smooth animation
                className="bg-white backdrop-blur-sm rounded-2xl overflow-hidden border border-input shadow-xl"
              >
                <motion.div
                  layout // layout ensures smooth animation
                  className="w-max h-full p-4"
                >
                  {children}
                </motion.div>
              </motion.div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export const ProductItem = ({
  title,
  description,
  href,
  icon,
}: {
  title: string;
  description?: string;
  href: string;
  src?: string;
  icon?: React.ElementType;
}) => {
  const Icon = icon as React.ElementType;

  return (
    <Link href={href} className="flex space-x-2 group">
      <div className="border border-gray-200 rounded-md p-4 w-full">
        {icon && <Icon className="w-10 h-10" />}
        <div>
          <h4 className="text-base font-semibold mb-0.5">
            {title}
          </h4>
          {description && (
            <p className="text-neutral-400 text-xs max-w-[14rem]">
              {description}
            </p>
          )}
          <HoveredLink
            className="mt-3 text-[13px] font-normal"
            href={href}
            noLink={true}
          >
            View Product
          </HoveredLink>
        </div>
      </div>
    </Link>
  );
};